import React, {useEffect, useRef} from 'react';

import {Button, Heading, Text, WithTooltip} from '@albert-io/atomic';
import type {WithTooltipHandle} from '@albert-io/atomic/molecules/Tooltip/Tooltip.react';

import type {HomeTourStepId} from './homeTourConfig';
import {HOME_TOUR_STEPS} from './homeTourConfig';
import type {HomeTour} from './useHomeTour';

/**
 * Overrides the tooltip's dark-theme chrome with the announcement treatment from the design:
 * `background/special` at Elevation/L3, no border, x1 radius, x2 padding. Each declaration in
 * `tooltip.scss` it replaces is `!important`, so these have to be too.
 */
const CALLOUT_CLASS =
  'background-special! elevation-shadow-l3! border-none! rounded-x1! p-x2! w-[255px]';

/** Same override for the caret, whose fill and silhouette borders are theme-driven pseudo-element styles. */
const CALLOUT_CARET_CLASS =
  'a-tooltip-caret [&::before]:background-special! [&::before]:border-none!';

/**
 * The class selector sits inside the sidebar's `overflow-y-auto` column, whose width is narrower
 * than the callout. Bounding to the viewport (as the sidemenu's own tooltips do) stops Popper from
 * squeezing or flipping it into the nav.
 */
const CALLOUT_ANCHOR_PROPS = {
  arrowClassName: CALLOUT_CARET_CLASS,
  modifiers: {preventOverflow: {boundariesElement: 'viewport'}}
} as const;

/**
 * The dim layer has to clear the global navigation (`$z-high + 1`, i.e. 1000, in
 * `global-navigation.scss`) so the whole chrome dims, and stay under the portal layer (9999) that
 * renders the callout itself. The highlighted anchor then sits one step above the dim layer.
 */
const OVERLAY_Z_CLASS = 'z-[1001]';
const SPOTLIT_ANCHOR_Z_CLASS = 'z-[1002]';

interface Props {
  children: React.ReactNode;
  /** Match the anchor's own width (the class selector spans the sidebar column). */
  fillWidth?: boolean;
  stepId: HomeTourStepId;
  tour: HomeTour;
}

/**
 * Wraps a sidemenu anchor and, while the tour is on `stepId`, dims the page behind it and shows the
 * announcement callout pointing at it. Renders `children` untouched for everyone outside the
 * announcement cohort, so the wrapper only exists for teachers who will actually see the tour.
 *
 * `behavior='click'` rather than the default `'hover'` so pointer movement can't open or close a
 * step; the dim layer plus the inert anchor mean the wrapper never receives the click either, which
 * leaves the tour's own state the only thing that decides what is showing.
 */
export const HomeTourCallout = ({children, fillWidth = false, stepId, tour}: Props) => {
  const {activeStepId, advance, dismiss, isEligible, isLastStep, stepNumber, totalSteps} = tour;
  const isActive = activeStepId === stepId;
  const tooltipRef = useRef<WithTooltipHandle>(null);

  // `WithTooltip` keeps its open state internally (driven by hover/click) and exposes no controlled
  // prop, so a tour-driven step change has to be pushed in through the imperative handle.
  useEffect(() => {
    if (isActive) {
      tooltipRef.current?.activate();
    } else {
      tooltipRef.current?.deactivate();
    }
  }, [isActive]);

  // The dim layer swallows clicks, so Escape is the only way out for keyboard users.
  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };
    global.document.addEventListener('keydown', handleKeyDown);
    return () => global.document.removeEventListener('keydown', handleKeyDown);
  }, [dismiss, isActive]);

  if (!isEligible) {
    return <>{children}</>;
  }

  const step = HOME_TOUR_STEPS.find((candidate) => candidate.id === stepId);
  if (!step) {
    return <>{children}</>;
  }

  const content = (
    <div
      aria-label={step.title}
      className='gap-x0-half flex flex-col items-end'
      data-testid={`home-tour-${stepId}`}
      role='dialog'
    >
      <Heading className='w-full' color='primary-inverse-static' size='xs'>
        {step.title}
      </Heading>
      <Text className='w-full' color='primary-inverse-static' size='m'>
        {step.body}
      </Text>
      <Button
        autoFocus
        color='blend-dark'
        data-testid='home-tour-advance'
        onClick={advance}
        size='s'
      >
        {isLastStep ? (
          'Done'
        ) : (
          <>
            {/* `.a-button` is inline-flex, so a literal space between the two labels collapses. */}
            Next
            <span className='ml-x0-half font-normal'>{`(${stepNumber} of ${totalSteps})`}</span>
          </>
        )}
      </Button>
    </div>
  );

  return (
    <>
      {isActive && (
        <div
          aria-hidden='true'
          className={`top-x0 left-x0 background-primary-inverse fixed! h-screen w-screen opacity-35 ${OVERLAY_Z_CLASS}`}
          data-testid='home-tour-overlay'
          role='presentation'
        />
      )}
      <WithTooltip
        anchoredFixedPositionProps={CALLOUT_ANCHOR_PROPS}
        behavior='click'
        content={content}
        display={fillWidth ? 'block' : 'inline-block'}
        enabled={isActive}
        placement='right-start'
        ref={tooltipRef}
        showArrow
        tooltipClassName={CALLOUT_CLASS}
        wrapperProps={{
          className: [
            fillWidth ? 'w-full' : '',
            // Lift the anchor out of the dim layer, and make it inert so a click on the highlighted
            // control can't dismiss the step without recording it.
            isActive ? `relative pointer-events-none ${SPOTLIT_ANCHOR_Z_CLASS}` : ''
          ]
            .filter(Boolean)
            .join(' ')
        }}
      >
        {children}
      </WithTooltip>
    </>
  );
};
